import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import emblem from "../assets/logo-emblem-dark.png";

/**
 * Compact intentional footer. No top margin — the parent page's own pb-*
 * already provides breathing room, so an extra `mt-*` here just stacks into a
 * dead black gap. Internal spacing is deliberately tight so the crest, contact
 * strip, sitemap links, and copyright feel like one block rather than four
 * floating rows.
 */
export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="border-t border-white/10 pt-6 pb-5 px-6 lg:px-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto">
        {/* Crest — emblem + bilingual wordmark, packed tight */}
        <div className="flex flex-col items-center text-center" data-testid="footer-wordmark">
          <img
            src={emblem}
            alt="Mazad Abu Dhabi"
            data-testid="footer-emblem"
            className="h-9 w-auto object-contain opacity-90 mb-2"
          />
          <bdi
            dir="rtl"
            data-testid="footer-wordmark-ar"
            className="block font-serif font-normal text-white leading-none text-lg md:text-xl"
          >
            مزاد أبوظبي
          </bdi>
          <div
            data-testid="footer-wordmark-en"
            className="mt-1 font-serif font-normal gold-gradient tracking-[0.28em] uppercase text-sm md:text-base"
          >
            Mazad AD
          </div>
          <div className="overline mt-1.5 text-white/50 text-[10px]">Abu Dhabi · UAE</div>
        </div>

        {/* Contact + Hours — one row on md+, stacked on mobile, minimal gutters */}
        <div
          className="mt-5 pt-5 border-t border-white/10 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-center"
          data-testid="footer-details"
        >
          <div>
            <div className="overline mb-1.5 text-[10px]">Contact</div>
            <p className="text-[13px] text-white/70">Abu Dhabi, United Arab Emirates · concierge@mazadad.ae</p>
            <p className="text-[13px] text-white/70 mt-1">
              <a href="https://instagram.com/mazad_ad" target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="footer-instagram">
                @mazad_ad
              </a>
              <span className="text-white/30 mx-1.5">·</span>
              <a href="https://wa.me/971557711188" target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="footer-whatsapp">
                WhatsApp 055 771 1188
              </a>
            </p>
          </div>
          <div>
            <div className="overline mb-1.5 text-[10px]">Hours</div>
            <p className="text-[13px] text-white/70">Friday Live Auction · 19:00 GST</p>
            <p className="text-[13px] text-white/70">Showroom by appointment</p>
          </div>
        </div>

        {/* Sitemap — legal, buyer-confidence, and brand pages. Four small
            columns on desktop, two on mobile, tight enough not to dominate
            the crest but visible enough that a first-time buyer can find
            Terms/Privacy/Returns without scrolling around. */}
        <div
          className="mt-5 pt-5 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 max-w-4xl mx-auto text-center md:text-left"
          data-testid="footer-sitemap"
        >
          <div>
            <div className="overline mb-2 text-[10px]">About</div>
            <ul className="space-y-1.5">
              <li>
                <Link to="/about" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-about">
                  About Mazad AD
                </Link>
              </li>
              <li>
                <Link to="/trust" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-trust">
                  Authentication
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="overline mb-2 text-[10px]">Buyer confidence</div>
            <ul className="space-y-1.5">
              <li>
                <Link to="/faq" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-faq">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/return-policy" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-return-policy">
                  Return Policy
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="overline mb-2 text-[10px]">Legal</div>
            <ul className="space-y-1.5">
              <li>
                <Link to="/terms" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-terms">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="overline mb-2 text-[10px]">Shop</div>
            <ul className="space-y-1.5">
              <li>
                <Link to="/catalog" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-catalog">
                  Marketplace
                </Link>
              </li>
              <li>
                <Link to="/auctions" className="text-[13px] text-white/70 hover:text-gold transition-colors" data-testid="footer-link-auctions">
                  Auctions
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-white/40 text-center">
          © {new Date().getFullYear()} Mazad AD. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
